import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Menu } from 'antd';
import './NavLanguage.scss';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { changeLanguage } from '../../../redux/actions/user.action';

const { SubMenu } = Menu;

// Default language list when mvp.listLanguage is empty
const defaultLanguageList = [
  { value: 'en', label: 'English' },
  { value: 'vi', label: 'Tiếng Việt' }
];

function NavLanguage({ user, mvp, changeLanguage }) {
  const [currentLanguage, setCurrentLanguage] = useState('vi');
  const [menuTitle, setMenuTitle] = useState('');
  const [listLanguage, setListLanguage] = useState([]);

  // Memoize mvpListLanguage to prevent infinite loops
  const mvpListLanguage = useMemo(() => {
    return mvp?.listLanguage || [];
  }, [mvp?.listLanguage]);

  useEffect(() => {
    // Nếu user chưa đăng nhập, lấy ngôn ngữ từ sessionStorage, mặc định là tiếng Anh
    if (!user?.accessToken || !user?.authenticated) {
      // Lấy ngôn ngữ từ sessionStorage, nếu không có thì mặc định 'en'
      const sessionLanguage = sessionStorage.getItem('ced:session-language');
      const language = sessionLanguage || 'en';
      setCurrentLanguage(language);
      
      // Sử dụng mvpListLanguage nếu có, nếu không thì dùng defaultLanguageList
      const languagesToUse = mvpListLanguage.length > 0 ? mvpListLanguage : defaultLanguageList;
      
      // Map lại listLanguage để hiển thị label theo ngôn ngữ hiện tại
      // Nếu đang ở English: hiển thị English / Vietnamese
      // Nếu đang ở Vietnamese: hiển thị Tiếng Việt / Tiếng Anh
      const mappedListLanguage = languagesToUse.map(lg => {
        if (language === 'en') {
          // Khi đang ở English, hiển thị label tiếng Anh
          return {
            value: lg.value,
            label: lg.value === 'en' ? 'English' : 'Vietnamese'
          };
        } else {
          // Khi đang ở Vietnamese, hiển thị label tiếng Việt
          return {
            value: lg.value,
            label: lg.value === 'en' ? 'Tiếng Anh' : 'Tiếng Việt'
          };
        }
      });
      setListLanguage(mappedListLanguage);
      const currentLanguageObj = mappedListLanguage.find(lg => lg.value === language);
      if (currentLanguageObj) {
        setMenuTitle(currentLanguageObj.label);
      }
      return;
    }

    // Nếu user đã đăng nhập, sử dụng ngôn ngữ từ user.language (từ DB)
    // user.language từ API có format {value: 'en'|'vi', label: 'English'|'Tiếng Việt'}
    let language = 'en'; // Mặc định là tiếng Anh
    if (user.language) {
      // Lấy value từ language object (từ API getInfoUser)
      if (typeof user.language === 'object' && user.language.value) {
        language = user.language.value;
      } else if (typeof user.language === 'string') {
        language = user.language;
      }
    }
    setCurrentLanguage(language);

    if (mvpListLanguage.length) {
      // Map listLanguage để hiển thị label theo ngôn ngữ hiện tại của user
      const mappedListLanguage = mvpListLanguage.map(lg => {
        if (language === 'en') {
          // Khi đang ở English, hiển thị label tiếng Anh
          return {
            value: lg.value,
            label: lg.value === 'en' ? 'English' : 'Vietnamese'
          };
        } else {
          // Khi đang ở Vietnamese, hiển thị label tiếng Việt
          return {
            value: lg.value,
            label: lg.value === 'en' ? 'Tiếng Anh' : 'Tiếng Việt'
          };
        }
      });
      setListLanguage(mappedListLanguage);
      const currentLanguageObj = mappedListLanguage.find(lg => lg.value === language);
      if (currentLanguageObj) {
        setMenuTitle(currentLanguageObj.label);
      }
    }
  }, [mvp?.listLanguage, user?.language, user?.accessToken, user?.authenticated]);

  const handleClick = async ({ key }) => {
    const language = listLanguage.find(lg => lg.value === key);

    if (!language) {
      return
    }
    setMenuTitle(language.label);

    if (user?.accessToken && user?.authenticated) {
      // User đã đăng nhập: cập nhật ngôn ngữ trong database
      try {
        const result = await changeLanguage(key);
        if (result.status === 'ok') {
          // Sau khi lưu thành công, reload để áp dụng ngôn ngữ mới
          // changeLanguage đã fetch lại user và cập nhật Redux state
          setTimeout(() => {
            window.location.reload()
          }, 100)
        } else {
          console.error('Failed to update language in database:', result.message);
        }
      } catch (error) {
        console.error('Failed to update language in database:', error);
      }
    } else {
      // User chưa đăng nhập: lưu vào sessionStorage (sẽ tự xóa khi đóng tab)
      sessionStorage.setItem('ced:session-language', key);
      // Also update localStorage for API calls
      localStorage.setItem('ced:language', key);
      
      // Đợi một chút để đảm bảo sessionStorage được lưu
      setTimeout(() => {
        window.location.reload()
      }, 100)
    }
  };

  return (
    <>
      {listLanguage.length ? <Menu
        onSelect={handleClick}
        selectedKeys={[currentLanguage]}
        className="menu-language"
        mode="horizontal"
        getPopupContainer={(triggerNode) => triggerNode.parentElement}
        theme="dark"
      >
        <SubMenu
          key="sub1"
          title={menuTitle}
          icon={<FontAwesomeIcon icon={faAngleDown} />}
          popupClassName="submenu-language"
        >
          {listLanguage.map(language =>
            <Menu.Item key={language.value}>{language.label}</Menu.Item>
          )}
        </SubMenu>
      </Menu>: null}
    </>
  );
};

const mapStateToProps = ({ user, mvp }) => ({ user, mvp });
const mapDispatchToProps = {
  changeLanguage
};

export default connect(mapStateToProps, mapDispatchToProps)(NavLanguage);
